import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslations } from "hooks";
import { getTranslation } from "common";
import DraggableTaskCard from "./DraggableTaskCard";
import {
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  Droppable,
} from "react-beautiful-dnd";
import { SequenceFlow, Task } from "model/activitiesModel";
import React, { useEffect, useMemo, useRef } from "react";
import noTaskDoing from "../image/img_doing.svg";
import TaskCard from "./TaskCard";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  DESCRIPTION_OFFSET,
  doesDescriptionNeedExtraHeight,
  // getOffsetHeightForParticipants,
  getOffsetHeightForTask,
  getParticipants,
  LABEL_OFFSET,
  TASK_CARD_HEIGHT,
} from "common/utilities";
interface DoingPanelProps {
  userId: number;
  all: boolean;
  loading: boolean;
  labelCompleteMode: boolean;
  switchChecked: boolean;
  administrator?: boolean;
  dragItem: Task | null;
  processedTasks: Task[];
  handleSequenceFlowChoice: (task: Task, sequenceFlow: SequenceFlow) => void;
  goTodo: (task: Task) => Promise<void>;
  goToDone: (task: Task) => Promise<void>;
  setDetail: (idprocesstoken: number) => void;
  view: 'minimal' | 'compact' | 'extended';
  showOnlyIdUserZero: boolean;
}
type RowProps = {
  index: number;
  style: Object;
};
const HEADER_HEIGHT = 65;
const DoingPanel: React.FC<DoingPanelProps> = ({
  handleSequenceFlowChoice,
  goToDone,
  goTodo,
  all,
  switchChecked,
  dragItem,
  labelCompleteMode,
  administrator,
  loading,
  setDetail,
  processedTasks,
  view,
  showOnlyIdUserZero,
}) => {
  const translations = useTranslations();
  const listRef = useRef<List<Task>>(null);

  const filteredTasks = useMemo(() => {
    //stessa cosa di TodoPanels
    const correctTask = showOnlyIdUserZero
      ? processedTasks.filter((task) => task.assignmentmode === 2)
      : processedTasks;
    // console.log('filteredTasks:', result.map((t, i) => `${t.idprocesstoken}-${i}`).join(', '));
    return correctTask;
  }, [showOnlyIdUserZero, processedTasks]);

  if (window.Cypress) {
    window.Cypress.on("scroll-doing", (idprocesstoken: number) =>
      listRef.current?.scrollToItem(idprocesstoken)
    );
  }
  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [processedTasks, labelCompleteMode, view]);

  const Row: React.FC<RowProps> = React.useMemo(
    () =>
      ({ index, style }) => {
        const task = processedTasks[index];
        if (!task) {
          return null;
        }
        return (
          <span style={style}>
            <DraggableTaskCard
              index={index}
              labelCompleteMode={labelCompleteMode}
              key={task.idprocesstoken}
              handleSequenceFlowChoice={handleSequenceFlowChoice}
              all={all}
              task={task}
              switchChecked={switchChecked}
              nextButtonLabel={getTranslation(
                translations,
                "pm.label.process_status.done"
              )}
              handleNextStep={goToDone}
              handleBackStep={goTodo}
              administrator={administrator}
              setDetail={(idprocesstoken) => setDetail(idprocesstoken)}
              view={view}
            />
          </span>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processedTasks, labelCompleteMode, view]
  );

  return (
    <Stack
      sx={{
        flex: 1,
        maxHeight: `100%`,
        backgroundColor: (_) => _.palette.grey[200],
        borderRadius: "15px",
      }}
    >
      <Box sx={{ height: HEADER_HEIGHT }}>
        <Typography sx={{ flex: 1, textAlign: "start" }} variant="h6">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              ml: "10px",
              mt: "6px",
            }}
          >
            <div
              style={{
                height: "10px",
                width: "10px",
                borderRadius: "50%",
                backgroundColor: "#FFA500",
                marginRight: "8px",
              }}
            />
            {getTranslation(
              translations,
              "pm.label.process_status.doing"
            )}
            <Chip
              label={processedTasks.length}
              sx={{ height: 30, fontSize: 15, fontWeight: 700, ml: 1 }}
            />
          </Box>
        </Typography>
        <Divider
          sx={{
            height: "3px",
            borderRadius: "10px",
            backgroundColor: "#FFA500",
            mt: 4,
            mb: 3,
            width: "85%",
            mx: "auto",
          }}
        />
      </Box>
      <Droppable
        isDropDisabled={dragItem?.state === 1}
        droppableId="doing"
        mode="virtual"
        renderClone={(
          provided: DraggableProvided,
          snapshot: DraggableStateSnapshot,
          rubric: DraggableRubric
        ) => {
          const taskId = parseInt(rubric.draggableId, 10);
          const task = filteredTasks.find(t => t.idprocesstoken === taskId);
          //da questi log si evinceva bene
          // console.log('DoingPanel dragging:', {
          //   draggableId: rubric.draggableId,
          //   sourceIndex: rubric.source.index,
          //   foundTask: task?.idprocesstoken
          // });
          return (
            <span {...provided.draggableProps} {...provided.dragHandleProps}>
              <TaskCard
                labelCompleteMode={labelCompleteMode}
                switchChecked={false}
                setDetail={(idprocesstoken) => setDetail(idprocesstoken)}
                task={task || dragItem || processedTasks[rubric.source.index]}
                view={view}
              />
            </span>
          );
        }}
      >
        {(provided, snapshot) => (
          <Box
            sx={{
              maxHeight: `calc(100% - ${HEADER_HEIGHT}px})`,
              height: `calc(100% - ${HEADER_HEIGHT}px)`,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              pb: 2,
            }}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {processedTasks.length <= 0 && !loading && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Box
                  component="img"
                  alt="..."
                  sx={{
                    width: "208px",
                    height: "208px",
                  }}
                  src={noTaskDoing}
                />
                <Typography color="textSecondary" component="p" sx={{ mt: 1 }}>
                  {getTranslation(
                    translations,
                    "generic.no_ongoing_activity.caption"
                  )}
                </Typography>
              </Box>
            )}
            {loading && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <CircularProgress />
              </Box>
            )}
            <AutoSizer style={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}>
              {({
                height,
                width,
              }: {
                height: number;
                width: number;
                scaledWidth: number;
                scaledHeight: number;
              }) => (
                <List
                  ref={listRef}
                  height={height}
                  itemCount={loading ? 0 : processedTasks.length}
                  itemSize={(index) => {
                    const task = processedTasks[index];

                    let spacing = 10;
                    let cardHeight = TASK_CARD_HEIGHT;

                    if (view === 'minimal') {
                      spacing = 4;
                      cardHeight = 60;
                      return cardHeight + spacing;
                    }

                    if (view === 'compact') {
                      spacing = 10;
                      cardHeight = 90;
                      return cardHeight + spacing + (doesDescriptionNeedExtraHeight(task.description) ? DESCRIPTION_OFFSET : 0);
                    }

                    return (
                      cardHeight +
                      spacing +
                      (labelCompleteMode ? LABEL_OFFSET : 0) +
                      getOffsetHeightForTask(task.userdataLength) +
                      // getOffsetHeightForParticipants(
                      //   getParticipants(task.jsonparticipant),
                      //   true
                      // ) +
                      (doesDescriptionNeedExtraHeight(task.description)
                        ? DESCRIPTION_OFFSET
                        : 0)
                    );
                  }}
                  width={width}
                  overscanCount={5}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          </Box>
        )}
      </Droppable>
    </Stack>
  );
};

export default DoingPanel;
